import { jwtDecode } from 'jwt-decode';
import React, { useContext, useEffect, useState } from 'react';
import { Circles } from 'react-loading-icons';
import { Rating } from 'react-simple-star-rating';
import { ReactComponent as StarsEmpty } from '../../../assets/images/StarSVGEmpty.svg';
import { ReactComponent as StarsFilled } from '../../../assets/images/StarSVGFilled.svg';
import {
  getLatestRating,
  getLatestRatingByUser,
} from '../../../util/DatabaseUtil';
import { getEducationRatingByQuestionId } from '../../../util/EducationUtil';
import {
  getEntertainmentRatingByQuestionId,
  visualRating,
} from '../../../util/EntertainmentUtil';
import { RatingContext } from '../RatingPageTabs';
import RatingProgressBar from '../../rating/ProgressBar';

export default function RatingSummary({ changeReview, refresh, setRefresh }) {
  const token = localStorage.getItem('loginToken');
  const userData = token ? jwtDecode(token) : '';
  const { categoryInformation, id } = useContext(RatingContext);
  const [latestRating, setLatestRating] = useState([]);

  useEffect(() => {
    const fetchLatestRating = async () => {
      try {
        if (id) {
          const entertainmentData = await getLatestRatingByUser(
            userData.id,
            id,
            categoryInformation.ID,
          );
          setLatestRating(entertainmentData);
        } else {
          const educationData = await getLatestRating(
            userData.id,
            categoryInformation.ID,
          );
          setLatestRating(educationData);
        }
      } catch (err) {
        console.error('Error Fetching Latest User Rating', err);
      }
    };
    fetchLatestRating();
  }, [categoryInformation.ID, userData.id, refresh, id]);

  return (
    <>
      {latestRating.ratings ? (
        <div className="mx-4 md:mx-10 lg:mx-20 my-10 bg-[#081325] rounded-3xl p-6 md:p-10 shadow-2xl border border-slate-800 text-white font-montserrat">
          <h3 className="text-2xl font-bold text-slate-100 mb-6 border-b border-slate-800 pb-4">
            Rating Summary
          </h3>

          <div className="flex justify-center mb-10 bg-[#111827] border border-slate-700/50 rounded-2xl p-6 shadow-lg">
            <RatingProgressBar
              value={
                latestRating.ratings.reduce((acc, ratingQue) => {
                  const currentScore = id
                    ? getEntertainmentRatingByQuestionId(
                        ratingQue.questionId,
                        latestRating.contentId,
                        latestRating.ratings,
                        id,
                      ) / 2
                    : getEducationRatingByQuestionId(
                        ratingQue._id,
                        latestRating.ratings,
                      ) / 2;
                  return acc + currentScore;
                }, 0) / (latestRating.ratings.length || 1)
              }
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 w-full">
            {latestRating.ratings.map((ratingQue, idx) => {
              const currentScore = id
                ? getEntertainmentRatingByQuestionId(
                    ratingQue.questionId,
                    latestRating.contentId,
                    latestRating.ratings,
                    id,
                  ) / 2
                : getEducationRatingByQuestionId(
                    ratingQue._id,
                    latestRating.ratings,
                  ) / 2;

              return (
                <div
                  key={idx}
                  className="bg-[#111827] border border-slate-700/50 rounded-2xl p-5 shadow-lg group"
                >
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 justify-between">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold shadow-inner ${visualRating(
                        currentScore,
                      )} text-white shrink-0`}
                    >
                      {currentScore}
                    </div>

                    <div className="flex-1 text-center sm:text-left">
                      <label className="text-slate-200 font-semibold mb-1 block">
                        {ratingQue.questionTitle}
                      </label>
                    </div>

                    <div className="shrink-0 scale-90 sm:scale-100 origin-right">
                      <Rating
                        initialValue={currentScore}
                        allowFraction
                        readonly
                        emptyIcon={<StarsEmpty className="inline-block" />}
                        fillIcon={
                          <StarsFilled className="inline-block drop-shadow-md" />
                        }
                        SVGclassName="inline-block"
                        SVGstorkeWidth={0}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-[#111827] rounded-3xl p-8 border border-slate-800 shadow-lg mt-10">
            <div className="flex flex-col items-center justify-center space-y-4">
              <label className="text-xl font-bold text-slate-100">
                Recommended
              </label>

              <div className="flex gap-4">
                <div
                  className={`flex flex-col items-center justify-center px-6 py-3 rounded-xl border-2 transition-all ${
                    latestRating.isRecommended === true
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                      : 'border-slate-700 bg-slate-800/50 text-slate-500'
                  }`}
                >
                  <span className="font-semibold">Yes</span>
                </div>
                <div
                  className={`flex flex-col items-center justify-center px-6 py-3 rounded-xl border-2 transition-all ${
                    latestRating.isRecommended === false
                      ? 'border-red-500 bg-red-500/10 text-red-400'
                      : 'border-slate-700 bg-slate-800/50 text-slate-500'
                  }`}
                >
                  <span className="font-semibold">No</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
            <button
              type="button"
              onClick={() => {
                changeReview();
                setRefresh(!refresh);
              }}
              className="px-8 py-3 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-colors font-semibold shadow-lg shadow-blue-500/20"
            >
              Change Review
            </button>

            <a href="/">
              <button
                type="button"
                className="px-12 py-3 rounded-xl text-slate-300 bg-slate-800 hover:bg-slate-700 border border-slate-700 transition-colors font-semibold"
              >
                Back
              </button>
            </a>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center py-20 min-h-[50vh]">
          <Circles stroke="#3b82f6" fill="#3b82f6" />
        </div>
      )}
    </>
  );
}
